import React from 'react';
import {addClasses, cn, mergeClasses} from '#lib/helpers/class.js';
import {isFunction} from '#lib/utils/function.js';

const TextareaContext = React.createContext();
TextareaContext.displayName = 'TextareaContext';

const useContext = () => React.useContext(TextareaContext);

function Helper(props) {
    const {as = 'p', className, id: providedId, children, ...rest} = props;
    const {helperId, hasError} = useContext();
    const id = providedId ?? helperId;

    const helper = React.createElement(
        as,
        {
            className: mergeClasses('mt-2 text-sm text-neutral-500', className),
            id,
            ...rest
        },
        children
    );

    return hasError ? null : helper;
}

function ErrorMessage(props) {
    const {as = 'p', className, id: providedId, children, ...rest} = props;
    const {errorMessageId} = useContext();
    const id = providedId ?? errorMessageId;

    return React.createElement(
        as,
        {
            className: mergeClasses('mt-1 text-sm text-red-800', className),
            id,
            ...rest
        },
        children
    );
}

function Label(props) {
    const {className, children, htmlFor: providedHtmlFor, ...rest} = props;
    const {textareaId, hasError} = useContext() ?? {};
    const htmlFor = providedHtmlFor ?? textareaId;
    return (
        <label
            className={mergeClasses(
                cn(
                    hasError ? 'text-red-700' : 'text-neutral-700',
                    'mb-1 block text-sm font-medium'
                ),
                className
            )}
            htmlFor={htmlFor}
            {...rest}
        >
            {children}
        </label>
    );
}

function _Area(props, ref) {
    const {
        autoSize = true,
        className,
        id: providedId,
        onInput: providedOnInput,
        ...rest
    } = props;

    const {textareaId} = useContext() ?? {};
    const id = providedId ?? textareaId;

    const [replicatedValue, setReplicatedValue] = React.useState('');

    const innerRef = React.useRef(null);
    React.useImperativeHandle(ref, () => innerRef.current);

    React.useEffect(() => {
        const textArea = innerRef.current;
        if (!textArea) return;
        setReplicatedValue(textArea.value);
    }, []);

    function handleInput(event) {
        if (isFunction(providedOnInput)) providedOnInput(event);
        if (!autoSize) return;
        setReplicatedValue(event.currentTarget.value);
    }

    return (
        <div
            className={mergeClasses(
                cn('textarea-container', autoSize && 'autosize'),
                className
            )}
            id={id}
            data-replicated-value={replicatedValue}
        >
            <textarea
                ref={innerRef}
                className="textarea"
                onInput={handleInput}
                aria-errormessage={useContext.errorMessageId}
                aria-describedby={useContext.helperId}
                {...rest}
            />
        </div>
    );
}

const Area = React.forwardRef(_Area);

function _Container(props) {
    const {as = 'div', className, children, ...rest} = props;
    return React.createElement(
        as,
        {className: mergeClasses('w-full', className), ...rest},
        children
    );
}

function _Full(props, ref) {
    const {className, label, children, error, helper, ...rest} = props;
    return (
        <_Container className={className}>
            {label && <Label>{label}</Label>}
            <Area ref={ref} {...rest}>
                {children}
            </Area>
            {helper && <Helper>{helper}</Helper>}
            {error && <ErrorMessage>{error}</ErrorMessage>}
        </_Container>
    );
}

const Full = React.forwardRef(_Full);

function ContextProvider(props) {
    const {children} = props;
    const textareaId = React.useId();
    const helperId = React.useId();
    const errorMessageId = React.useId();
    return (
        <TextareaContext.Provider value={{textareaId, helperId, errorMessageId}}>
            {children}
        </TextareaContext.Provider>
    );
}

function WithContext(Element, props) {
    const {hasError: controlledHasError, error: controlledError, ...rest} = props;
    return (
        <ContextProvider
            controlledHasError={controlledHasError}
            controlledError={controlledError}
        >
            <Element {...rest} />
        </ContextProvider>
    );
}

function GiveContext(Element) {
    return WithContext.bind(null, Element);
}

const Container = GiveContext(_Container);
Container.Helper = Helper;
Container.ErrorMessage = ErrorMessage;
Container.Label = Label;
Container.Area = Area;
Container.Full = Full;
export default Container;
